// @copyright  2006-2026 West Indies Software Engineering Company Limited. All rights reserved.

export default {
    '$dir': 'ltr',

    // `…tasks.title` and `…tasks.titleShared` were deleted on 2026-08-13 (user
    // ruling: no page title on any client portal action bar). The comment
    // below still describes the set it was written for; the heading line it
    // mentions is the one that went.
    'portals.clients.tasks.empty': 'Nothing outstanding. We will let you know when something is needed.',
    'portals.clients.tasks.overdue': 'Overdue',
    'portals.clients.tasks.dueOn': 'Due {date}',
    'portals.clients.tasks.documentNeeded': 'Document needed',
    'portals.clients.tasks.documentSent': 'Document sent',
    // What is still wanted, said on the row (`FR-09`, 2026-08-13). The message
    // figure is a TOTAL, never an unread count: read state was decided against
    // (`NG-01`), so nothing here knows when the client last looked and a label
    // implying otherwise would be a lie.
    'portals.clients.tasks.answeredOfTotal': '{answered} of {total} answered',
    'portals.clients.tasks.messageCount': '{count} messages',
    // `…tasks.showCompleted` was deleted on 2026-08-13 (user ruling: "Turn Show
    // Completed into a primary action button. That loads the completed tasks on
    // its own page"). The checkbox it labelled is gone; `…tasks.completedTasks`
    // below is the action that replaced it.
    'portals.clients.tasks.error': 'We could not load your tasks. Try again.',
    'portals.clients.tasks.noAccess': 'You do not have access to this portal.',

    // Added by the task list screen. The catalogue above is the specified set;
    // these cover the states and markers the screen actually has to put into
    // words — a heading when more than one accountant is asking, a row's own
    // "who asked", a task with no date, and the two buttons.
    'portals.clients.tasks.askedBy': 'Asked by {practice}',
    'portals.clients.tasks.noDueDate': 'No date given',
    'portals.clients.tasks.doneMarker': 'Done',
    'portals.clients.tasks.loading': 'Loading what your accountant needs from you.',
    'portals.clients.tasks.tryAgain': 'Try again',
    'portals.clients.tasks.showMore': 'Show more',
    'portals.clients.tasks.noAccessHelp': 'If you think this is wrong, get in touch with your accountant.',
    'portals.clients.tasks.emptyHeading': 'You are all up to date',
    'portals.clients.tasks.openTask': 'Open {title}',

    // Added by the completed-work screen (user ruling, 2026-08-13). The empty
    // state is deliberately NOT the open list's reassurance: "You are all up to
    // date" said here would tell a client with outstanding work that they have
    // none. `backToOpen` repeats the wording of `…task.backToTasks` rather than
    // borrowing it — the detail screen's way back and this one's are the same
    // sentence today and are free to stop being so.
    'portals.clients.tasks.completedTasks': 'Completed tasks',
    'portals.clients.tasks.backToOpen': 'Back to your tasks',
    'portals.clients.tasks.completedLoading': 'Loading the work you have finished.',
    'portals.clients.tasks.completedEmptyHeading': 'Nothing finished yet',
    'portals.clients.tasks.completedEmpty': 'Work you have marked done will appear here.',
    'portals.clients.tasks.completedError': 'We could not load your finished work. Try again.',

    'portals.clients.task.filesSent': 'Files you have sent',
    'portals.clients.task.noFilesYet': '(none yet)',
    'portals.clients.task.chooseFile': 'Choose a file',
    'portals.clients.task.allowedTypes': 'PDF, spreadsheet, document or image. Up to {limit}.',
    'portals.clients.task.uploading': 'Sending {filename}…',
    'portals.clients.task.uploadFailed': '{filename} did not send. Try again.',
    'portals.clients.task.typeRejected': '{filename} cannot be sent. Please use a PDF, spreadsheet, document or image.',
    'portals.clients.task.tooLarge': '{filename} is too large. The limit is {limit}.',
    'portals.clients.task.markDone': 'Mark as done',
    'portals.clients.task.markDoneBlocked': 'Available once you have sent at least one file.',
    'portals.clients.task.done': 'Done. Your accountant has been notified.',
    'portals.clients.task.readOnly': 'This work has ended. You can still see what was sent.',

    // Added by the task detail screen. The catalogue above is the specified set;
    // these cover what the detail screen has to put into words and the list did
    // not — the way back, the three ways a single task can fail to load, the two
    // read-only variants, and every step of an upload the client watches happen.
    'portals.clients.task.backToTasks': 'Back to your tasks',
    'portals.clients.task.loading': 'Loading what your accountant has asked for.',
    'portals.clients.task.error': 'We could not load this request. Try again.',
    'portals.clients.task.notFound': 'We could not find that request.',
    'portals.clients.task.notFoundHelp': 'It may have been withdrawn. Go back to your list, or get in touch with your accountant.',
    'portals.clients.task.actionFailed': 'That did not work. Try again.',
    'portals.clients.task.completedOn': 'You marked this done on {date}.',
    'portals.clients.task.completedNoDate': 'You have marked this done.',
    'portals.clients.task.marking': 'Marking this done…',
    'portals.clients.task.sentOn': 'Sent {date}',
    'portals.clients.task.uploadsHeading': 'Send a file',
    'portals.clients.task.uploadProgress': '{percent}% sent',
    'portals.clients.task.uploadPreparing': 'Getting ready to send {filename}…',
    'portals.clients.task.uploaded': '{filename} has been sent.',
    'portals.clients.task.emptyFile': '{filename} is empty. Please choose the file again.',
    'portals.clients.task.retryUpload': 'Try sending it again',
    'portals.clients.task.discardUpload': 'Remove {filename} from this list',

    // Added by the questions the accountant puts on a task (user ruling,
    // 2026-08-13). A task carries a SET of them, each with its own answer type,
    // so the wording here is deliberately plural throughout — a client with one
    // question and a client with five read the same screen.
    //
    // `markDoneBlockedQuestions` names a COUNT rather than saying "answer the
    // questions": a client looking at five and a disabled button needs to know
    // how many are still owed, and the optional ones are not among them.
    'portals.clients.task.questionsHeading': 'What your accountant needs',
    'portals.clients.task.optionalQuestion': 'Optional',
    'portals.clients.task.notAnswered': 'Not answered',
    'portals.clients.task.answeredOn': 'Answered {date}',
    'portals.clients.task.sendResponses': 'Send responses',
    'portals.clients.task.sendingResponses': 'Sending your responses…',
    'portals.clients.task.responsesSent': 'Your responses have been sent.',
    'portals.clients.task.responsesFailed': 'Your responses did not send. Try again.',
    'portals.clients.task.markDoneBlockedQuestions': 'Available once you have answered what your accountant is still waiting on.',
    'portals.clients.task.answerYes': 'Yes',
    'portals.clients.task.answerNo': 'No',
    'portals.clients.tasks.answeredOfTotal': '{answered} of {total} answered',
};
